import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css'; // Import the styles
import {
  alpha,
  Box,
  Button,
  Pagination,
  Paper,
  IconButton,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton as MuiIconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery
} from '@mui/material';
import DisplayQuillContent from './DisplayQuillContent';
import { Edit, Delete, CloseRounded, AddRounded, TableRowsRounded } from '@mui/icons-material';
import { FlowBreadcrumbs, FlowPageHeader, FlowStatGrid, SoftPanel, truncateText } from './FlowLayout';

const DynamicTablePage = () => {
  const { tableName, pageTitle, viewType } = useParams(); // viewType can be 'portrait' or 'landscape'
  const [tableData, setTableData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [columns, setColumns] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false); // Loading state for submission
  const limit = 20;
  const [previews, setPreviews] = useState({});
  const baseApiUrl = 'https://aaryanholidays.in/backend/';
  const baseImagePath = 'https://aaryanholidays.in/express/uploads/';
  const isMobile = useMediaQuery('(max-width:900px)');

  useEffect(() => {
    fetchData();
  }, [tableName, currentPage, search]);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${baseApiUrl}table-data/${tableName}`, {
        params: { page: currentPage, search },
      });
      const filteredData = response.data.data.filter((row) => row.id !== 1);
      setTableData(filteredData);
      setTotalRecords(response.data.totalRecords - 1);

      if (response.data.data.length > 0) {
        setColumns(Object.keys(response.data.data[0]));
      }
    } catch (error) {
      console.error('Error fetching table data:', error);
    }
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleOpenModal = (record = null) => {
    if (record) {
      setEditMode(true);
      setFormData(record);
    } else {
      setEditMode(false);
      setFormData({});
    }
    setModalOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, files } = e.target;

    if (files && files[0]) {
      const file = files[0];
      setFormData({ ...formData, [name]: file });

      // Generate a preview URL for the selected file and store it in previews state
      const fileUrl = URL.createObjectURL(file);
      setPreviews((prev) => ({ ...prev, [name]: fileUrl }));
    } else {
      setFormData({ ...formData, [name]: e.target.value });
    }
  };
  const handleQuillChange = (name, value) => {
    setFormData((formData) => ({
      ...formData,
      [name]: value,
    }));
  };
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); // Start loading

    const formDataObj = new FormData();
    Object.keys(formData).forEach((key) => formDataObj.append(key, formData[key]));

    try {
      if (editMode) {
        await axios.put(`${baseApiUrl}table-data/${tableName}/${formData.id}`, formDataObj);
      } else {
        await axios.post(`${baseApiUrl}table-data/${tableName}`, formDataObj);
      }
      setModalOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error saving data:', error);
    } finally {
      setLoading(false); // Stop loading after request completes
    }
  };

  const handleDeleteRecord = async (id) => {
    if (window.confirm('Are you sure you want to delete this record?')) {
      try {
        await axios.delete(`${baseApiUrl}table-data/${tableName}/${id}`);
        fetchData();
      } catch (error) {
        console.error('Error deleting record:', error);
      }
    }
  };

  const renderField = (column, value) => {
    if (column.startsWith('image_')) {
      return <img src={`${baseImagePath}/${value}`} alt="image" width="100" />;
    } else if (column.startsWith('video_')) {
      return (
        <video width="100" controls>
          <source src={`${baseImagePath}/${value}`} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      );
    } else if (column.startsWith('pdf_')) {
      return (
        <a href={`${baseImagePath}/${value}`} target="_blank" rel="noopener noreferrer">
          Download PDF
        </a>
      );
    }
    else
    {
      return (
        <DisplayQuillContent content={value} />
      );
    }
    return value;
  };

  const formatLabel = (column) => {
    return column
      .replace(/^image_|^video_|^pdf_/, '')
      .replace(/_/g, ' ')
      .toUpperCase();
  };

  const summarizeValue = (column, value) => {
    if (column === 'id') {
      return value;
    }

    if (column.startsWith('image_')) {
      return <img src={`${baseImagePath}/${value}`} alt="preview" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 16 }} />;
    }

    if (column.startsWith('video_')) {
      return (
        <video style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 16 }} controls>
          <source src={`${baseImagePath}/${value}`} type="video/mp4" />
        </video>
      );
    }

    if (column.startsWith('pdf_')) {
      return 'PDF file';
    }

    return truncateText(String(value || '').replace(/<[^>]*>/g, ''), 120);
  };

  const renderModalField = (column) => {
    const label = formatLabel(column);

    return (
      <Box
        key={column}
        sx={{
          p: 2,
          borderRadius: 3,
          border: '1px solid rgba(148, 163, 184, 0.16)',
          backgroundColor: '#f8fafc'
        }}
      >
        <Typography variant="overline" sx={{ color: '#64748b', letterSpacing: '0.08em' }}>
          {label}
        </Typography>
        <Box sx={{ mt: 1.25 }}>
          {column.startsWith('text_') && (
            <TextField
              name={column}
              value={formData[column] || ''}
              onChange={handleInputChange}
              fullWidth
              multiline
              minRows={column.includes('description') ? 3 : 1}
            />
          )}
          {column.startsWith('image_') && (
            <Stack spacing={1.5}>
              {previews[column] ? (
                <img src={previews[column]} alt="preview" style={{ width: '100%', maxWidth: 220, borderRadius: 16 }} />
              ) : (
                formData[column] && (
                  <img src={`${baseImagePath}/${formData[column]}`} alt="preview" style={{ width: '100%', maxWidth: 220, borderRadius: 16 }} />
                )
              )}
              <input type="file" name={column} onChange={handleInputChange} />
            </Stack>
          )}
          {column.startsWith('video_') && (
            <Stack spacing={1.5}>
              {previews[column] ? (
                <video style={{ width: '100%', maxWidth: 260, borderRadius: 16 }} controls>
                  <source src={previews[column]} type="video/mp4" />
                </video>
              ) : (
                formData[column] && (
                  <video style={{ width: '100%', maxWidth: 260, borderRadius: 16 }} controls>
                    <source src={`${baseImagePath}/${formData[column]}`} type="video/mp4" />
                  </video>
                )
              )}
              <input type="file" name={column} onChange={handleInputChange} />
            </Stack>
          )}
          {column.startsWith('pdf_') && (
            <Stack spacing={1.5}>
              {previews[column] ? (
                <a href={previews[column]} target="_blank" rel="noopener noreferrer">
                  View PDF
                </a>
              ) : (
                formData[column] && (
                  <a href={`${baseImagePath}/${formData[column]}`} target="_blank" rel="noopener noreferrer">
                    View PDF
                  </a>
                )
              )}
              <input type="file" name={column} onChange={handleInputChange} />
            </Stack>
          )}
          {!(column.startsWith('image_') || column.startsWith('video_') || column.startsWith('pdf_') || column.startsWith('text_')) && (
            <Box
              sx={{
                '& .ql-toolbar': {
                  borderTopLeftRadius: 12,
                  borderTopRightRadius: 12,
                  borderColor: alpha('#94a3b8', 0.35),
                  backgroundColor: '#ffffff'
                },
                '& .ql-container': {
                  minHeight: 180,
                  borderBottomLeftRadius: 12,
                  borderBottomRightRadius: 12,
                  borderColor: alpha('#94a3b8', 0.35),
                  backgroundColor: '#ffffff'
                },
                '& .ql-editor': {
                  minHeight: 140,
                  maxHeight: 260,
                  overflowY: 'auto'
                }
              }}
            >
              <ReactQuill
                name={column}
                value={formData[column] || ''}
                onChange={(value) => handleQuillChange(column, value)}
                theme="snow"
              />
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  // Render the landscape table (default table view)
  const renderLandscapeTable = () => (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell key={column}>{formatLabel(column)}</TableCell>
            ))}
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tableData.map((row, index) => (
            <TableRow key={index}>
              {columns.map((column) => (
                <TableCell key={`${column}-${index}`}>{renderField(column, row[column])}</TableCell>
              ))}
              <TableCell>
                <IconButton onClick={() => handleOpenModal(row)}>
                  <Edit />
                </IconButton>
                <IconButton onClick={() => handleDeleteRecord(row.id)}>
                  <Delete />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // Render the portrait table (one table per row)
  const renderPortraitTable = () => (
    <Stack spacing={2}>
      {tableData.map((row, index) => (
        <Box
          key={index}
          sx={{
            p: 2,
            borderRadius: 4,
            border: '1px solid rgba(148, 163, 184, 0.16)',
            backgroundColor: '#ffffff',
            boxShadow: '0 12px 26px rgba(15, 23, 42, 0.05)'
          }}
        >
          <Stack spacing={1.5}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                Record {row.id || index + 1}
              </Typography>
              <Stack direction="row" spacing={0.5}>
                <IconButton onClick={() => handleOpenModal(row)}>
                  <Edit />
                </IconButton>
                <IconButton onClick={() => handleDeleteRecord(row.id)}>
                  <Delete />
                </IconButton>
              </Stack>
            </Stack>
            {columns.map((column) => (
              <Box key={`${column}-${index}`}>
                <Typography variant="overline" sx={{ color: '#64748b', letterSpacing: '0.08em' }}>
                  {formatLabel(column)}
                </Typography>
                <Box sx={{ mt: 0.75 }}>{summarizeValue(column, row[column])}</Box>
              </Box>
            ))}
          </Stack>
        </Box>
      ))}
    </Stack>
  );

  return (
    <Box sx={{ p: 2 }}>
      <FlowBreadcrumbs items={[{ label: 'Data Tables' }, { label: pageTitle }]} />

      <FlowPageHeader
        title={`${pageTitle} Data`}
        subtitle="Review records, search quickly, and open a cleaner editor for each entry."
        search={search}
        searchPlaceholder={`Search ${pageTitle.toLowerCase()} records`}
        onSearchChange={handleSearchChange}
        action={(
          <Button
            variant="contained"
            startIcon={<AddRounded />}
            sx={{ borderRadius: 999, px: 2.5, py: 1.2, textTransform: 'none', fontWeight: 700 }}
            onClick={() => handleOpenModal()}
          >
            Add Record
          </Button>
        )}
      />

      <FlowStatGrid
        items={[
          {
            label: 'Records',
            value: totalRecords,
            icon: TableRowsRounded,
            tone: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)'
          }
        ]}
      />

      <SoftPanel>
        {viewType === 'potrait' || isMobile ? renderPortraitTable() : renderLandscapeTable()}
      </SoftPanel>

      <Box mt={2} display="flex" justifyContent="center">
        <Pagination
          count={Math.ceil(totalRecords / limit)}
          page={currentPage}
          onChange={handlePageChange}
          color="primary"
        />
      </Box>

      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        fullScreen={isMobile}
        fullWidth
        maxWidth="lg"
        scroll="paper"
        PaperProps={{
          sx: {
            display: 'flex',
            flexDirection: 'column',
            borderRadius: isMobile ? 0 : 5,
            overflow: 'hidden',
            minHeight: isMobile ? '100vh' : 'auto',
            maxHeight: isMobile ? '100vh' : '88vh'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1.5, fontWeight: 800 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {editMode ? 'Edit Record' : 'Add Record'}
            </Typography>
            <MuiIconButton
              onClick={() => setModalOpen(false)}
              sx={{
                width: 38,
                height: 38,
                border: '1px solid rgba(148, 163, 184, 0.2)',
                backgroundColor: '#ffffff',
                '&:hover': {
                  backgroundColor: '#f8fafc'
                }
              }}
            >
              <CloseRounded fontSize="small" />
            </MuiIconButton>
          </Stack>
        </DialogTitle>
        <Divider />
        <form onSubmit={handleFormSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <DialogContent
            dividers={false}
            sx={{
              flex: 1,
              minHeight: 0,
              p: { xs: 2, md: 3 },
              overflowY: 'auto',
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(59,130,246,0.45) transparent',
              '&::-webkit-scrollbar': {
                width: 10
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent'
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(59,130,246,0.32)',
                borderRadius: 999,
                border: '2px solid transparent',
                backgroundClip: 'padding-box'
              },
              '&::-webkit-scrollbar-thumb:hover': {
                backgroundColor: 'rgba(37,99,235,0.48)'
              }
            }}
          >
            <Stack spacing={2.25}>
              {columns.filter((column) => column !== 'id').map((column) => renderModalField(column))}
            </Stack>
          </DialogContent>
          <Divider />
          <DialogActions sx={{ px: { xs: 2, md: 3 }, py: 2, justifyContent: 'space-between', backgroundColor: '#ffffff' }}>
            <Button
              onClick={() => setModalOpen(false)}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 999,
                px: 2.5,
                color: '#334155'
              }}
            >
              Cancel
            </Button>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minWidth={120}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Button
                variant="contained"
                color="primary"
                type="submit"
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 999,
                  px: 3.2,
                  py: 1.15,
                  background: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)',
                  boxShadow: '0 18px 34px rgba(37, 99, 235, 0.22)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #0284c7 0%, #1d4ed8 100%)'
                  }
                }}
              >
                {editMode ? 'Save Changes' : 'Add Record'}
              </Button>
            )}
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default DynamicTablePage;


